import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";

function rotationRowId(row) {
    return row?.id ?? row?.key;
}

function rotationTimeValue(row) {
    const stamp = row?.std || row?.std_utc || row?.etd || "";
    const parsed = Date.parse(stamp);
    return Number.isFinite(parsed) ? parsed : 0;
}

function compareRotationFlights(left, right) {
    const leftIndex = Number(left?.rotation_index);
    const rightIndex = Number(right?.rotation_index);
    if (Number.isFinite(leftIndex) && Number.isFinite(rightIndex) && leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
    }
    const leftLeg = Number(left?.rotation_leg_index);
    const rightLeg = Number(right?.rotation_leg_index);
    if (Number.isFinite(leftLeg) && Number.isFinite(rightLeg) && leftLeg !== rightLeg) {
        return leftLeg - rightLeg;
    }
    return rotationTimeValue(left) - rotationTimeValue(right);
}

function isSystemRotationNo(rotationNo) {
    return /^(ROT|PART)-/i.test(String(rotationNo || "").trim());
}

function rotationDisplayNumber(rotationNo) {
    const text = String(rotationNo || "").trim();
    if (!text) {
        return "";
    }
    const last = text.split("-").pop();
    const numeric = Number.parseInt(last, 10);
    return Number.isFinite(numeric) ? numeric : last;
}

/**
 * 轮换可视化组件
 * 用于图形化展示航班轮换信息，包括轮换状态、位置和关系
 *
 * @param {Object} props
 * @param {Object} props.flight - 当前航班数据
 * @param {Array} props.allFlights - 所有航班数据，用于计算轮换位置关系
 * @param {String} props.baseAirport - 基地机场代码，用于确定轮换起始/终止位置，默认为null
 */
const RotationVisualizer = ({ flight, allFlights = [], baseAirport = null }) => {
    const [rotationInfo, setRotationInfo] = useState({
        isStart: false,
        isEnd: false,
        isComplete: false,
        isLonelyFlight: false,
        rotationFlights: [],
        position: "middle",
    });

    useEffect(() => {
        if (!flight || !flight.rotation_no) {
            setRotationInfo({
                isStart: false,
                isEnd: false,
                isComplete: false,
                isLonelyFlight: false,
                rotationFlights: [],
                position: "none",
            });
            return;
        }

        const rotationFlights = (allFlights || [])
            .filter((item) => item.rotation_no === flight.rotation_no && item.aircraft_reg === flight.aircraft_reg)
            .slice()
            .sort(compareRotationFlights);

        let isStart = false;
        let isEnd = false;

        if (baseAirport) {
            isStart = flight.departure_airport === baseAirport;
            isEnd = flight.arrival_airport === baseAirport;
        } else {
            const currentId = rotationRowId(flight);
            isStart = rotationFlights.length > 0 && rotationRowId(rotationFlights[0]) === currentId;
            isEnd = rotationFlights.length > 0 && rotationRowId(rotationFlights[rotationFlights.length - 1]) === currentId;
        }

        const isLonelyFlight = rotationFlights.length === 1;
        const isComplete =
            flight.is_rotation_complete !== undefined && flight.is_rotation_complete !== null
                ? flight.is_rotation_complete
                : isSystemRotationNo(flight.rotation_no)
                  ? String(flight.rotation_no).startsWith("ROT-")
                  : true;

        let position = "middle";
        if (isStart && isEnd) position = "complete";
        else if (isStart) position = "start";
        else if (isEnd) position = "end";

        setRotationInfo({
            isStart,
            isEnd,
            isComplete,
            isLonelyFlight,
            rotationFlights,
            position,
        });
    }, [flight, allFlights, baseAirport]);

    if (!flight || !flight.rotation_no) {
        return <span>-</span>;
    }

    const rotationColor = rotationInfo.isComplete ? "#1890ff" : "#faad14";
    const rotationNumber = rotationDisplayNumber(flight.rotation_no);

    let borderStyle = {};
    let containerStyle = {
        height: "42px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
    };

    if (rotationInfo.isLonelyFlight || (rotationInfo.isStart && rotationInfo.isEnd)) {
        borderStyle = {
            border: `2px solid ${rotationColor}`,
            width: "18px",
            height: "24px",
            borderRadius: "4px",
        };
    } else if (rotationInfo.isStart) {
        containerStyle.justifyContent = "flex-end";
        borderStyle = {
            borderLeft: `2px solid ${rotationColor}`,
            borderTop: `2px solid ${rotationColor}`,
            borderRight: `2px solid ${rotationColor}`,
            borderBottom: "none",
            width: "18px",
            height: "36px",
            borderRadius: "4px 4px 0 0",
        };
    } else if (rotationInfo.isEnd) {
        containerStyle.justifyContent = "flex-start";
        borderStyle = {
            borderLeft: `2px solid ${rotationColor}`,
            borderBottom: `2px solid ${rotationColor}`,
            borderRight: `2px solid ${rotationColor}`,
            borderTop: "none",
            width: "18px",
            height: "36px",
            borderRadius: "0 0 4px 4px",
        };
    } else {
        borderStyle = {
            borderLeft: `2px solid ${rotationColor}`,
            borderRight: `2px solid ${rotationColor}`,
            borderTop: "none",
            borderBottom: "none",
            width: "18px",
            height: "100%",
            position: "absolute",
        };
    }

    return (
        <Tooltip
            title={
                <div>
                    <div style={{ fontSize: "14px", fontWeight: "bold" }}>轮换编号: {flight.rotation_no}</div>
                    <div style={{ fontSize: "12px", marginTop: 4, color: flight.booking_no ? "#fff" : "rgba(255,255,255,0.45)" }}>
                        {flight.booking_no ? `订单号: ${flight.booking_no}` : "暂无订单号"}
                    </div>
                </div>
            }
            mouseEnterDelay={1.2}
            placement="right"
        >
            <div style={{ textAlign: "center" }}>
                <div style={containerStyle}>
                    <div style={borderStyle}></div>
                    <span
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: rotationColor,
                        }}
                    >
                        {rotationNumber}
                    </span>
                </div>
            </div>
        </Tooltip>
    );
};

export default RotationVisualizer;
